// TimeSelectionDisplayView.tsx
import React from "react";
import styles from "./TimeSelectionView.module.css";
import { TimeSelectionRow, DayPartSelection } from "./types";
import TimeRangeRowView from "./TimeRangeRowView";
import DayPartIconButton from "./DayPartIconButton";
import { formatDateToLabel } from "./TimeFormatter";

interface TimeSelectionDisplayViewProps {
    mode: "time" | "daypart";
    /** For time mode: the selected time rows to display */
    selectedTimes?: TimeSelectionRow[];
    /** For daypart mode: the selected daypart selections to display */
    selectedDayParts?: DayPartSelection[];
    /** If true, group daypart entries by date in the UI. Default false. */
    groupByDate?: boolean;
    /** If true, icons will appear with selected styling */
    daypartIconSelected?: boolean;
    noGap?: boolean;
}

const TimeSelectionDisplayView: React.FC<TimeSelectionDisplayViewProps> = ({
    mode,
    selectedTimes,
    selectedDayParts,
    groupByDate = false,
    daypartIconSelected = true,
    noGap = false,
}) => {
    if (mode === "time") {
        // In time mode, simply display each selected time row read‑only.
        return (
            <div className={styles.timeSelectionTable}>
                {selectedTimes?.map((row) => (
                    <div key={row.id} className={styles.row}>
                        <TimeRangeRowView startTime={row.startTime} endTime={row.endTime} />
                    </div>
                ))}
            </div>
        );
    } else {
        // In daypart mode, display the selected dayparts.
        if (!selectedDayParts || selectedDayParts.length === 0) {
            return <div>No daypart selections available.</div>;
        }

        if (groupByDate) {
            // Group selections by date
            const grouped = selectedDayParts.reduce<Record<string, DayPartSelection[]>>((acc, sel) => {
                if (!acc[sel.selectedDate]) {
                    acc[sel.selectedDate] = [];
                }
                acc[sel.selectedDate].push(sel);
                return acc;
            }, {});

            return (
                <div className={`${styles.daySelectionTable} ${noGap ? '' : styles.displayGap}`}>
                    {Object.entries(grouped).map(([dateString, selections]) => (
                        <div key={dateString} className={`${styles.dateRow} ${styles.readOnly} ${noGap ? styles.noRowGap : ''}`}>
                            <div className={styles.dateLabel}>{formatDateToLabel(dateString)}</div>
                            <div className={styles.dayPartRow}>
                                {selections.map((sel, idx) => (
                                    // Render the DayPartIconButton in a disabled or unclickable state.
                                    // Here we simply pass an empty onClick handler.
                                    <DayPartIconButton
                                        key={idx}
                                        selected={daypartIconSelected}
                                        onClick={() => { }}
                                        dayPart={sel.selectedDayPart}
                                        readOnly={true}
                                        bannerText={""} // or pass banner text if needed
                                    />
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            );
        } else {
            // No grouping: render each selection on its own row.
            return (
                <div className={`${styles.daySelectionTable} ${noGap ? '' : styles.displayGap}`}>
                    {selectedDayParts.map((sel, idx) => (
                        <div key={idx} className={`${styles.dateRow} ${styles.readOnly}`}>
                            <div className={styles.dateLabel}>{formatDateToLabel(sel.selectedDate)}</div>
                            <DayPartIconButton
                                selected={daypartIconSelected}
                                onClick={() => { }}
                                dayPart={sel.selectedDayPart}
                                readOnly={true}
                                bannerText={""}
                            />
                        </div>
                    ))}
                </div>
            );
        }
    }
};

export default TimeSelectionDisplayView;
